import { Injectable } from '@angular/core'
import { BehaviorSubject, Observable, Subject, of, from } from 'rxjs'
import { catchError, map, mergeMap, takeUntil, toArray } from 'rxjs/operators'
import { IosbuildService } from '../ios-build/iosbuild.service'
import { BuilderService } from 'src/app/builder-services/builder.service'
import { SettingsApiService } from 'src/app/builder-services/api/settings.api.service'
import { IosAutomationOptions, IosAutomationProgress, IosBuildResult, IosBuildSummary } from './automatic-build.types'

@Injectable({
  providedIn: 'root'
})
export class IosBuildAutomationService {
  // Cancellation support
  private cancelSubject = new Subject<void>()
  private isCancelled = false

  private isRunningSubject = new BehaviorSubject<boolean>(false)
  readonly isRunning$ = this.isRunningSubject.asObservable()

  // Current automation options
  private currentOptions: IosAutomationOptions = { whatsNew: '' }

  // Polling interval in milliseconds
  private readonly POLL_INTERVAL = 8000

  private progressSubject = new BehaviorSubject<IosAutomationProgress>({
    currentIndex: 0,
    totalProjects: 0,
    currentProject: '',
    currentBundleId: '',
    currentBuildProgress: 0,
    currentBuildStatus: '',
    results: []
  })
  readonly progress$ = this.progressSubject.asObservable()

  get isRunning(): boolean {
    return this.isRunningSubject.getValue()
  }

  get progress(): IosAutomationProgress {
    return this.progressSubject.getValue()
  }

  constructor(
    private builderService: BuilderService,
    private settingsApi: SettingsApiService,
    private iosBuildService: IosbuildService
  ) { }

  /**
   * Cancels the ongoing automation process
   */
  cancel = (): void => {
    this.isCancelled = true
    this.cancelSubject.next()
  }

  /**
   * Resets the automation state for a new run
   */
  private reset = (): void => {
    this.isCancelled = false
    this.cancelSubject = new Subject<void>()
    this.progressSubject.next({
      currentIndex: 0,
      totalProjects: 0,
      currentProject: '',
      currentBundleId: '',
      currentBuildProgress: 0,
      currentBuildStatus: '',
      results: []
    })
  }

  /**
   * Main automation method: Fetches purchase projects from backend,
   * retrieves settings from Supabase, increments versions, and triggers builds for each valid project.
   * @param email User email to fetch purchase projects
   * @param options Automation options including whatsNew
   * @returns Observable with build summary
   */
  runAutomatedBuilds = (email: string, options: IosAutomationOptions): Observable<IosBuildSummary> => {
    this.reset()
    this.isRunningSubject.next(true)
    this.currentOptions = options

    return this.builderService.getPurchaseProjects(email).pipe(
      takeUntil(this.cancelSubject),
      mergeMap((projectsObj: any) => {
        // Convert object response to array
        const projects = projectsObj ? Object.values(projectsObj) : []

        if (!projects || projects.length === 0) {
          this.isRunningSubject.next(false)
          return of({ total: 0, completed: [], skipped: [], cancelled: [], failed: [] } as IosBuildSummary)
        }

        this.progressSubject.next({ ...this.progressSubject.getValue(), totalProjects: projects.length })

        // Process each project sequentially
        return from(projects).pipe(
          takeUntil(this.cancelSubject),
          mergeMap((project: any, index) => {
            if (this.isCancelled) {
              return of({
                projectId: project.id || 'unknown',
                bundleId: 'unknown',
                status: 'cancelled',
                reason: 'Automation cancelled by user'
              } as IosBuildResult)
            }
            const current = this.progressSubject.getValue()
            this.progressSubject.next({
              ...current,
              currentIndex: index + 1,
              currentProject: project.id || 'unknown',
              currentBuildProgress: 0,
              currentBuildStatus: ''
            })
            return this.processProject(project)
          }, 1), // concurrency of 1
          toArray(),
          map((results: IosBuildResult[]) => {
            this.isRunningSubject.next(false)
            return this.generateSummary(results)
          })
        )
      }),
      catchError((error) => {
        console.error('Failed to fetch projects:', error)
        this.isRunningSubject.next(false)
        return of({ total: 0, completed: [], skipped: [], cancelled: [], failed: [] } as IosBuildSummary)
      })
    )
  }

  /**
   * Processes a single project: fetches settings, gets versions, increments, and triggers build
   */
  private processProject = (project: any): Observable<IosBuildResult> => {
    const projectId = project.id

    if (this.isCancelled) {
      const result: IosBuildResult = {
        projectId: projectId || 'unknown',
        bundleId: 'unknown',
        status: 'cancelled',
        reason: 'Automation cancelled by user'
      }
      const current = this.progressSubject.getValue()
      this.progressSubject.next({ ...current, results: [...current.results, result] })
      return of(result)
    }

    if (!projectId) {
      const result: IosBuildResult = {
        projectId: 'unknown',
        bundleId: 'unknown',
        status: 'skipped',
        reason: 'Project has no ID'
      }
      const current = this.progressSubject.getValue()
      this.progressSubject.next({ ...current, results: [...current.results, result] })
      return of(result)
    }

    // Step 1: Get general settings from Supabase to extract bundleId
    return from(this.settingsApi.getSettingsByType(projectId, 'general_settings')).pipe(
      takeUntil(this.cancelSubject),
      mergeMap((settings: any) => {
        if (this.isCancelled) {
          const result: IosBuildResult = {
            projectId,
            bundleId: 'unknown',
            status: 'cancelled',
            reason: 'Automation cancelled by user'
          }
          const current = this.progressSubject.getValue()
          this.progressSubject.next({ ...current, results: [...current.results, result] })
          return of(result)
        }

        // iOS uses bundle_id (same as package_name in most cases)
        const bundleId = settings?.bundle_id || settings?.package_name
        this.progressSubject.next({ ...this.progressSubject.getValue(), currentBundleId: bundleId || 'unknown' })

        if (!bundleId) {
          const result: IosBuildResult = {
            projectId,
            bundleId: 'unknown',
            status: 'skipped',
            reason: 'No bundle_id in general_settings'
          }
          const current = this.progressSubject.getValue()
          this.progressSubject.next({ ...current, results: [...current.results, result] })
          return of(result)
        }

        // Skip default bundle IDs
        if (bundleId === 'com.stacks.www') {
          const result: IosBuildResult = {
            projectId,
            bundleId,
            status: 'skipped',
            reason: `Default bundle ID (${bundleId}) - not configured`
          }
          const current = this.progressSubject.getValue()
          this.progressSubject.next({ ...current, results: [...current.results, result] })
          return of(result)
        }

        // Step 2: Check if app is published on App Store
        return this.iosBuildService.checkAppPublished(bundleId, projectId).pipe(
          takeUntil(this.cancelSubject),
          mergeMap((publishedResponse) => {
            if (this.isCancelled) {
              const result: IosBuildResult = {
                projectId,
                bundleId,
                status: 'cancelled',
                reason: 'Automation cancelled by user'
              }
              const current = this.progressSubject.getValue()
              this.progressSubject.next({ ...current, results: [...current.results, result] })
              return of(result)
            }
            console.log(publishedResponse);

            // Check if app is published OR in "Prepare for Submission" state
            const appStoreState = publishedResponse.appStoreState
            const isLiveOrPreparing = publishedResponse.isPublished || appStoreState === 'PREPARE_FOR_SUBMISSION'

            if (!isLiveOrPreparing) {
              const result: IosBuildResult = {
                projectId,
                bundleId,
                status: 'skipped',
                reason: publishedResponse.error || `App is not published or preparing for submission (state: ${appStoreState || 'unknown'})`
              }
              const current = this.progressSubject.getValue()
              this.progressSubject.next({ ...current, results: [...current.results, result] })
              return of(result)
            }

            // Step 3: Get app versions from App Store Connect
            return this.iosBuildService.getAppVersions(bundleId, projectId).pipe(
              takeUntil(this.cancelSubject),
              mergeMap((response) => {
                if (this.isCancelled) {
                  const result: IosBuildResult = {
                    projectId,
                    bundleId,
                    status: 'cancelled',
                    reason: 'Automation cancelled by user'
                  }
                  const current = this.progressSubject.getValue()
                  this.progressSubject.next({ ...current, results: [...current.results, result] })
                  return of(result)
                }

                // Use live version if available, otherwise use preparing version
                const liveVersion = response.liveVersion
                const preparingVersion = response.preparingVersion
                const targetVersion = liveVersion || preparingVersion

                // Check if any usable version exists
                if (!targetVersion?.versionString) {
                  const result: IosBuildResult = {
                    projectId,
                    bundleId,
                    status: 'skipped',
                    reason: 'No live or preparing version found on App Store'
                  }
                  const current = this.progressSubject.getValue()
                  this.progressSubject.next({ ...current, results: [...current.results, result] })
                  return of(result)
                }

                const currentVersionString = this.sanitizeVersionString(targetVersion.versionString)

                // Step 4: Increment version
                const newVersionString = this.incrementVersion(currentVersionString)

                // Step 5: Trigger build and poll for progress
                return this.startBuildAndPollProgress(projectId, bundleId, newVersionString, currentVersionString)
              }),
              catchError((error) => {
                const result: IosBuildResult = {
                  projectId,
                  bundleId,
                  status: 'skipped',
                  reason: `Failed to get app versions: ${error.message || 'Unknown error'}`
                }
                const current = this.progressSubject.getValue()
                this.progressSubject.next({ ...current, results: [...current.results, result] })
                return of(result)
              })
            )
          }),
          catchError((error) => {
            const result: IosBuildResult = {
              projectId,
              bundleId,
              status: 'skipped',
              reason: `Failed to check app publish status: ${error.message || 'Unknown error'}`
            }
            const current = this.progressSubject.getValue()
            this.progressSubject.next({ ...current, results: [...current.results, result] })
            return of(result)
          })
        )
      }),
      catchError((error) => {
        const result: IosBuildResult = {
          projectId,
          bundleId: 'unknown',
          status: 'skipped',
          reason: `Failed to get settings: ${error.message || 'Unknown error'}`
        }
        const current = this.progressSubject.getValue()
        this.progressSubject.next({ ...current, results: [...current.results, result] })
        return of(result)
      })
    )
  }

  /**
   * Starts a build and polls for progress until completion or failure
   */
  private startBuildAndPollProgress = (
    projectId: string,
    bundleId: string,
    newVersionString: string,
    oldVersionString: string
  ): Observable<IosBuildResult> => {
    this.progressSubject.next({ ...this.progressSubject.getValue(), currentBuildStatus: 'Starting build...', currentBuildProgress: 0 })

    return this.iosBuildService.startBuild(projectId, parseFloat(newVersionString) as any).pipe(
      takeUntil(this.cancelSubject),
      mergeMap(() => {
        // Build started successfully, now poll for progress
        return this.pollBuildProgress(projectId, newVersionString).pipe(
          mergeMap((pollResult) => {
            // Build failed - return result
            if (!pollResult.success) {
              const result: IosBuildResult = {
                projectId,
                bundleId,
                status: 'failed',
                reason: pollResult.error,
                oldVersion: oldVersionString,
                newVersion: newVersionString,
                buildProgress: pollResult.progress,
                ipaUrl: pollResult.ipaUrl
              }
              const current = this.progressSubject.getValue()
              this.progressSubject.next({ ...current, results: [...current.results, result] })
              return of(result)
            }

            // Build succeeded
            const result: IosBuildResult = {
              projectId,
              bundleId,
              status: 'completed',
              oldVersion: oldVersionString,
              newVersion: newVersionString,
              buildProgress: 100,
              ipaUrl: pollResult.ipaUrl
            }
            const current = this.progressSubject.getValue()
            this.progressSubject.next({ ...current, results: [...current.results, result] })
            return of(result)
          })
        )
      }),
      catchError((error) => {
        const result: IosBuildResult = {
          projectId,
          bundleId,
          status: 'failed',
          reason: `Build start failed: ${error.message || 'Unknown error'}`,
          oldVersion: oldVersionString,
          newVersion: newVersionString
        }
        const current = this.progressSubject.getValue()
        this.progressSubject.next({ ...current, results: [...current.results, result] })
        return of(result)
      })
    )
  }

  /**
   * Polls the build progress until completion, failure, or cancellation
   */
  private pollBuildProgress = (projectId: string, version: string): Observable<{ success: boolean; progress: number; error?: string; ipaUrl?: string }> => {
    return new Observable((observer) => {
      const poll = () => {
        if (this.isCancelled) {
          observer.next({ success: false, progress: 0, error: 'Cancelled by user' })
          observer.complete()
          return
        }

        this.iosBuildService.getProgress(projectId, version).subscribe(
          (res: any) => {
            const progress = Number(res?.data) || 0
            const status = res?.status || ''
            const ipaUrl = res?.ios_url

            this.progressSubject.next({ ...this.progressSubject.getValue(), currentBuildProgress: progress, currentBuildStatus: status })

            // Check for completion or error
            if (status === 'error' || progress >= 100 || status === 'ready' || status === 'complete') {
              if (status === 'error') {
                // Try to get error details
                this.iosBuildService.getBuildError(projectId).subscribe({
                  next: (errorRes: any) => {
                    const errorMsg = errorRes?.data?.error || errorRes?.message || 'Build failed'
                    observer.next({ success: false, progress, error: errorMsg })
                    observer.complete()
                  },
                  error: () => {
                    observer.next({ success: false, progress, error: 'Build failed with unknown error' })
                    observer.complete()
                  }
                })
              } else {
                observer.next({ success: true, progress: 100, ipaUrl })
                observer.complete()
              }
            } else {
              // Continue polling
              setTimeout(poll, this.POLL_INTERVAL)
            }
          },
          () => {
            observer.next({ success: false, progress: 0, error: 'Failed to get build progress' })
            observer.complete()
          }
        )
      }

      // Start polling
      poll()
    })
  }

  /**
   * Sanitizes a version string by removing quotes, extra spaces, and invalid characters
   * e.g., '"" 4.32""' → '4.32'
   */
  private sanitizeVersionString = (version: string): string => {
    if (!version) {
      return ''
    }
    // Keep only digits and dots, removing all other characters (quotes, spaces, etc.)
    return version.replace(/[^0-9.]/g, '')
  }

  /**
   * Increments a version by 0.1
   * e.g., "1.0" → "1.1", "2.5" → "2.6"
   */
  incrementVersion = (version: string): string => {
    if (!version) {
      return '1.0'
    }

    const currentVersion = parseFloat(version)

    if (!isNaN(currentVersion)) {
      return (currentVersion + 0.1).toFixed(1)
    }

    // Default fallback
    return '1.0'
  }

  /**
   * Generates a summary from the build results
   */
  private generateSummary = (results: IosBuildResult[]): IosBuildSummary => {
    const completed = results.filter((r) => r.status === 'completed')
    const skipped = results.filter((r) => r.status === 'skipped')
    const cancelled = results.filter((r) => r.status === 'cancelled')
    const failed = results.filter((r) => r.status === 'failed')

    return {
      total: results.length,
      completed,
      skipped,
      cancelled,
      failed
    }
  }

  /**
   * Prints a formatted summary to the console
   */
  printSummary = (summary: IosBuildSummary): void => {
    console.log('\n========== iOS BUILD AUTOMATION SUMMARY ==========')
    console.log(`Total Projects: ${summary.total}`)
    console.log(`Completed Builds: ${summary.completed.length}`)
    console.log(`Failed Builds: ${summary.failed.length}`)
    console.log(`Skipped Projects: ${summary.skipped.length}`)
    console.log(`Cancelled: ${summary.cancelled.length}`)

    if (summary.completed.length > 0) {
      console.log('\n--- COMPLETED BUILDS ---')
      summary.completed.forEach((build) => {
        console.log(`  ✓ ${build.bundleId} (ID: ${build.projectId})`)
        console.log(`    Version: ${build.oldVersion} → ${build.newVersion}`)
        if (build.ipaUrl) console.log(`    IPA: ${build.ipaUrl}`)
      })
    }

    if (summary.failed.length > 0) {
      console.log('\n--- FAILED BUILDS ---')
      summary.failed.forEach((build) => {
        console.log(`  ✗ ${build.bundleId} (ID: ${build.projectId})`)
        console.log(`    Reason: ${build.reason}`)
      })
    }

    if (summary.skipped.length > 0) {
      console.log('\n--- SKIPPED PROJECTS ---')
      summary.skipped.forEach((build) => {
        console.log(`  ○ ${build.bundleId || build.projectId}`)
        console.log(`    Reason: ${build.reason}`)
      })
    }

    if (summary.cancelled.length > 0) {
      console.log('\n--- CANCELLED ---')
      summary.cancelled.forEach((build) => {
        console.log(`  ⊘ ${build.bundleId || build.projectId}`)
      })
    }

    console.log('\n====================================================\n')
  }

  /**
   * Convenience method to run automation and print summary
   */
  runAndPrintSummary = (email: string, options?: IosAutomationOptions): Observable<IosBuildSummary> => {
    const opts = options || { whatsNew: '' }
    return this.runAutomatedBuilds(email, opts).pipe(
      map((summary) => {
        this.printSummary(summary)
        return summary
      })
    )
  }
}
